import * as React from 'react'
import ja from 'date-fns/locale/ja'
import * as classNames from 'classnames'
import { UseFormRegister } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faQuestion } from '@fortawesome/free-solid-svg-icons'

import { InformationText } from '~/atoms/text/InformationText'
import { InputText } from '~/atoms/form/InputText'
import { InputSelect } from '~/atoms/form/InputSelect'
import { InputRadio } from '~/atoms/form/InputRadio'
import { InputSubmit } from '~/atoms/form/InputSubmit'
import {
  dateToStringWithFormat,
  formatDateString,
  calcAgeWithDateString,
} from '@/utils/dateFormat'
import { isNurseryAccept } from '@/utils/reserve'

import * as Style from './style.css'

// サポートの種類
export const REQUEST_TYPE_SITTER = 'request_sitter'
export const REQUEST_TYPE_PICKUP_ONLY = 'request_pickup_only'
export const REQUEST_TYPE_SICK = 'request_sick'
export const REQUEST_TYPE_ILLNESS = 'request_illness'
export const REQUEST_TYPE_HANDICAP = 'request_handicap'
export const REQUEST_TYPE_DOULA = 'request_doula'
export const REQUEST_TYPE_TUTOR = 'request_tutor'
export const REQUEST_TYPE_HOUSEKEEPING = 'request_housekeeping'

// 利用助成の種類
export const PRICE_TYPE_DEFAULT = 'price_default'
export const PRICE_TYPE_HOUSEKEEPING_SUBSIDY = 'price_housekeeping_subsidy'

// 予約の種類
export const RESERVE_PLAN_CASUAL_INTERVIEW = 'casual_interview'
export const RESERVE_PLAN_INTERVIEW = 'interview'
export const RESERVE_PLAN_SOPT = 'spot'
export const RESERVE_PLAN_TOKYO_BS_TMP_USE = 'tokyo_bs_tmp_use'

// オンラインサポート
export const SUPPORT_STYLE_OFFLINE = 'offline'
export const SUPPORT_STYLE_ONLINE = 'online'

// コメント
const MAX_LENGTH_COMMENT = 4000
const EXAMPLE_RESERVE_COMMENT1 =
  '自宅でサポートをお願いしたいです。\n事前にお伝えしておくことがありましたらメッセージでご連絡ください。どうぞよろしくお願いいたします。\n【サポート中にしていただきたいこと】'
const EXAMPLE_RESERVE_COMMENT2 =
  'お迎えと夜までのサポートをお願いできればと思います。\n事前にお伝えしておくことがありましたらメッセージでご連絡ください。\nどうぞよろしくお願いいたします。\n\n【スケジュール】\n●時　保育園へお迎え\n●時　夕食\n●時　寝かしつけ\n\n【サポート開始時　引き継ぎの有無】\n有/無\n\n【サポート終了時　引き継ぎ先】\n母/父'
const EXAMPLE_HK_RESERVE_COMMENT1 =
  '掃除用具は、\n掃除機/キッチン用スポンジ/食器洗剤/お風呂洗剤/お風呂用スポンジ/トイレ洗剤/トイレブラシ/トイレ用シート/その他（　　）があります。'
const EXAMPLE_HK_RESERVE_COMMENT2 =
  '調理器具は、\nコンロは、●口で、ガス/IHです。\n鍋/フライパン/ボール/包丁/その他（　　）があります。\n保存容器は、●個あります。'

export interface IProps {
  defaultStartDate: Date
  defaultEndDate: Date
  dateRange: Date[]
  hourRange: string[]
  showSitter: boolean
  enableSitter: boolean
  showPickup: boolean
  enablePickup: boolean
  showSick: boolean
  enableSick: boolean
  showIllness: boolean
  enableIllness: boolean
  showHandicap: boolean
  enableHandicap: boolean
  showDoula: boolean
  enableDoula: boolean
  showTutor: boolean
  enableTutor: boolean
  showHousekeeping: boolean
  enableHousekeeping: boolean
  showTokyoBsTmpUse: boolean
  showTutorInformation: boolean
  showInterview: boolean
  canSpotRequst: boolean
  canTokyoBsTmpUse: boolean
  showHousekeepingSubsidy: boolean
  disabledHousekeepingSubsidy: boolean
  showSupportStyle: boolean
  showOnlineSupportInformation: boolean
  showTokyoBsInformation: boolean
  showTokyoBsCaution: boolean
  isInterview: boolean
  inputStartDateString: string
  inputRequestType: IRequestType
  inputCasualInterviewFlag: number
  inputInterviewFlag: number
  inputTokyoBsTmpUse: number
  inputSupportStyle: typeof SUPPORT_STYLE_OFFLINE | typeof SUPPORT_STYLE_ONLINE
  inputComment: string
  children: IReserveChild[]
  sitterProfile: IReserveSitterProfile
  lastReserveComment: string
  lastHkReserveComment: string
  canSubmit?: boolean
  register: UseFormRegister<IReserveRequestForm>
  changeRequestTypeHandler: (requestType: IRequestType) => void
  changeReserveTypeHandler: (resreveType: string) => void
  changeSupportStyleHandler: (supportStyle: string) => void
  clickExampleCommentHandler: (comment: string) => void
  submitHandler: () => void
  cancelHandler: () => void
}

export const TimeTmpHolding = ({
  defaultStartDate,
  defaultEndDate,
  dateRange,
  hourRange,
  showSitter,
  enableSitter,
  showPickup,
  enablePickup,
  showSick,
  enableSick,
  showIllness,
  enableIllness,
  showHandicap,
  enableHandicap,
  showDoula,
  enableDoula,
  showTutor,
  enableTutor,
  showHousekeeping,
  enableHousekeeping,
  showTokyoBsTmpUse,
  showTutorInformation,
  showInterview,
  canSpotRequst,
  canTokyoBsTmpUse,
  showHousekeepingSubsidy,
  disabledHousekeepingSubsidy,
  showSupportStyle,
  showOnlineSupportInformation,
  showTokyoBsInformation,
  showTokyoBsCaution,
  isInterview,
  inputStartDateString,
  inputRequestType,
  inputCasualInterviewFlag,
  inputInterviewFlag,
  inputTokyoBsTmpUse,
  inputSupportStyle,
  inputComment,
  children,
  sitterProfile,
  lastReserveComment,
  lastHkReserveComment,
  canSubmit,
  register,
  changeRequestTypeHandler,
  changeReserveTypeHandler,
  changeSupportStyleHandler,
  clickExampleCommentHandler,
  submitHandler,
  cancelHandler,
}: IProps): JSX.Element => {
  return (
    <form className={Style.TimeTmpHolding} onSubmit={submitHandler}>
      <section className={Style.TimeTmpHoldingSection}>
        <div>
          <div className={Style.TimeTmpHoldingRowInformation}>
            <InformationText
              color="notice"
              title="以下の項目をご入力の上、予約リクエストをご送信ください"
            />
          </div>
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                サポート開始日時
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              <div className={Style.TimeTmpHoldingFormSelect}>
                <InputSelect
                  withIcon={true}
                  defaultValue={dateToStringWithFormat(
                    defaultStartDate,
                    'yyyy-MM-dd',
                  )}
                  {...register('reserve.plan_start_date')}
                >
                  {dateRange.map((day, index) => (
                    <option
                      key={`plan-start-date-${index}`}
                      value={dateToStringWithFormat(day, 'yyyy-MM-dd')}
                    >
                      {dateToStringWithFormat(day, 'yyyy-MM-dd(E)', ja)}
                    </option>
                  ))}
                </InputSelect>
              </div>
              <div className={Style.TimeTmpHoldingFormSelect}>
                <InputSelect
                  withIcon={true}
                  defaultValue={dateToStringWithFormat(
                    defaultStartDate,
                    'HH:mm',
                  )}
                  {...register('reserve.plan_start_time')}
                >
                  {hourRange.map((hour, index) => (
                    <option key={`plan-start-time-${index}`} value={hour}>
                      {hour}
                    </option>
                  ))}
                </InputSelect>
              </div>
            </div>
          </div>
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                サポート終了日時
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              <div className={Style.TimeTmpHoldingFormSelect}>
                <InputSelect
                  withIcon={true}
                  defaultValue={dateToStringWithFormat(
                    defaultEndDate,
                    'yyyy-MM-dd',
                  )}
                  {...register('reserve.plan_end_date')}
                >
                  {dateRange.map((day, index) => (
                    <option
                      key={`plan-end-date-${index}`}
                      value={dateToStringWithFormat(day, 'yyyy-MM-dd')}
                    >
                      {dateToStringWithFormat(day, 'yyyy-MM-dd(E)', ja)}
                    </option>
                  ))}
                </InputSelect>
              </div>
              <div className={Style.TimeTmpHoldingFormSelect}>
                <InputSelect
                  withIcon={true}
                  defaultValue={dateToStringWithFormat(defaultEndDate, 'HH:mm')}
                  {...register('reserve.plan_end_time')}
                >
                  {hourRange.map((hour, index) => (
                    <option key={`plan-end-time-${index}`} value={hour}>
                      {hour}
                    </option>
                  ))}
                </InputSelect>
              </div>
            </div>
          </div>
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                サポートの種類
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              {showSitter && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="ベビーシッター"
                    value={REQUEST_TYPE_SITTER}
                    disabled={!enableSitter}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_SITTER)
                    }
                  />
                </div>
              )}
              {showPickup && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="送迎サポート"
                    value={REQUEST_TYPE_PICKUP_ONLY}
                    disabled={!enablePickup}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_PICKUP_ONLY)
                    }
                  />
                </div>
              )}
              {showSick && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="病児保育"
                    value={REQUEST_TYPE_SICK}
                    disabled={!enableSick}
                    {...register('reserve.request_type')}
                    onChange={() => changeRequestTypeHandler(REQUEST_TYPE_SICK)}
                  />
                </div>
              )}
              {showIllness && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="病後児保育"
                    value={REQUEST_TYPE_ILLNESS}
                    disabled={!enableIllness}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_ILLNESS)
                    }
                  />
                </div>
              )}
              {showHandicap && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="障がい児保育"
                    value={REQUEST_TYPE_HANDICAP}
                    disabled={!enableHandicap}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_HANDICAP)
                    }
                  />
                </div>
              )}
              {showDoula && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="産前産後ケア"
                    value={REQUEST_TYPE_DOULA}
                    disabled={!(enableDoula && inputTokyoBsTmpUse !== 1)}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_DOULA)
                    }
                  />
                </div>
              )}
              {showTutor && (
                <>
                  <div className={Style.TimeTmpHoldingFormRadio}>
                    <InputRadio
                      label="家庭教師"
                      value={REQUEST_TYPE_TUTOR}
                      disabled={!(enableTutor && inputTokyoBsTmpUse !== 1)}
                      {...register('reserve.request_type')}
                      onChange={() =>
                        changeRequestTypeHandler(REQUEST_TYPE_TUTOR)
                      }
                    />
                  </div>
                  {showTutorInformation && (
                    <div className={Style.TimeTmpHoldingInformation}>
                      <div className={Style.TimeTmpHoldingInformationTitle}>
                        福利厚生について
                      </div>
                      <div className={Style.TimeTmpHoldingInformationText}>
                        <span>
                          家庭教師サポートでは、福利厚生はご利用いただけません。
                        </span>
                        <br />
                        <span>
                          福利厚生・育児補助券ご利用をご検討の場合は「
                        </span>
                        <a href="/help/center_detail/subsidy">
                          キッズラインで使える助成制度と福利厚生サービス
                        </a>
                        <span>」をご確認ください。</span>
                      </div>
                    </div>
                  )}
                </>
              )}
              {showHousekeeping && (
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="家事代行"
                    value={REQUEST_TYPE_HOUSEKEEPING}
                    disabled={!(enableHousekeeping && inputTokyoBsTmpUse !== 1)}
                    {...register('reserve.request_type')}
                    onChange={() =>
                      changeRequestTypeHandler(REQUEST_TYPE_HOUSEKEEPING)
                    }
                  />
                </div>
              )}
            </div>
          </div>
          {showHousekeepingSubsidy && (
            <div className={Style.TimeTmpHoldingFormRow}>
              <div className={Style.TimeTmpHoldingFormRowName}>
                <div className={Style.TimeTmpHoldingFormItemTitle}>
                  利用助成の種類
                </div>
              </div>
              <div className={Style.TimeTmpHoldingFormRowBoby}>
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="家事支援割引(実証事業)"
                    value={PRICE_TYPE_HOUSEKEEPING_SUBSIDY}
                    disabled={disabledHousekeepingSubsidy}
                    {...register('reserve.price_type')}
                  />
                </div>
                <div
                  className={Style.TimeTmpHoldingInformation}
                  style={{ marginTop: '8px' }}
                >
                  <div className={Style.TimeTmpHoldingInformationTitle}>
                    <a
                      className={Style.TimeTmpHoldingInformationTitleLink}
                      href="https://kidsline.me/information/kaji_shien2024"
                      target="_blank"
                    >
                      家事支援割引(実証事業)について
                    </a>
                  </div>
                  <div className={Style.TimeTmpHoldingInformationText}>
                    家事代行の利用料金（定価税抜）のうち、3分の2が補助される福利厚生制度です。ご利用の場合はこちらをご選択ください。
                  </div>
                </div>
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="利用しない"
                    value={PRICE_TYPE_DEFAULT}
                    {...register('reserve.price_type')}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                予約の種類
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              {showInterview && (
                <div className={Style.TimeTmpHoldingDescription}>
                  初めて依頼するサポーターです。まずは顔合わせ・事前面談の予約をしましょう。
                </div>
              )}
              <div>
                {showInterview && (
                  <>
                    <div className={Style.TimeTmpHoldingFormRadio}>
                      <div className={Style.TimeTmpHoldingFormRadioContainer}>
                        <InputRadio
                          label="顔合わせ"
                          value={RESERVE_PLAN_CASUAL_INTERVIEW}
                          name="reserve_type"
                          checked={inputCasualInterviewFlag === 1}
                          onChange={() =>
                            changeReserveTypeHandler(
                              RESERVE_PLAN_CASUAL_INTERVIEW,
                            )
                          }
                        />
                        <label htmlFor="what_casual_interview">
                          <FontAwesomeIcon
                            icon={faQuestion}
                            className={Style.TimeTmpHoldingFormRadioInfoIcon}
                          />
                        </label>
                      </div>
                      <input
                        className={Style.TimeTmpHoldingFormRadioCtrl}
                        id="what_casual_interview"
                        type="checkbox"
                      />
                      <div className={Style.TimeTmpHoldingFormRadioInfo}>
                        実際のサポート前に、オンラインビデオ通話でサポーターとお互いの相性の確認や、お願いしたい内容の相談を行います。30分〜、無料で予約可能です。
                      </div>
                    </div>
                    <div className={Style.TimeTmpHoldingFormRadio}>
                      <div className={Style.TimeTmpHoldingFormRadioContainer}>
                        <InputRadio
                          label="事前面談"
                          value={RESERVE_PLAN_INTERVIEW}
                          name="reserve_type"
                          checked={inputInterviewFlag === 1}
                          onChange={() =>
                            changeReserveTypeHandler(RESERVE_PLAN_INTERVIEW)
                          }
                        />
                        <label htmlFor="what_interview">
                          <FontAwesomeIcon
                            icon={faQuestion}
                            className={Style.TimeTmpHoldingFormRadioInfoIcon}
                          />
                        </label>
                      </div>
                      <input
                        className={Style.TimeTmpHoldingFormRadioCtrl}
                        id="what_interview"
                        type="checkbox"
                      />
                      <div className={Style.TimeTmpHoldingFormRadioInfo}>
                        実際のサポート前に、サポーターとお子さまとの相性をしっかりチェックしたい方向けの予約です。親御さま立ち会いのもと、お試しでサポートを行います。1時間〜予約可能です。
                      </div>
                    </div>
                  </>
                )}
                <div className={Style.TimeTmpHoldingFormRadio}>
                  <InputRadio
                    label="単発の予約"
                    value={RESERVE_PLAN_SOPT}
                    name="reserve_type"
                    disabled={!canSpotRequst}
                    checked={
                      inputCasualInterviewFlag === 0 &&
                      inputInterviewFlag === 0 &&
                      inputTokyoBsTmpUse === 0
                    }
                    onChange={() => changeReserveTypeHandler(RESERVE_PLAN_SOPT)}
                  />
                </div>
                {showTokyoBsTmpUse && (
                  <div className={Style.TimeTmpHoldingFormRadio}>
                    <InputRadio
                      label="東京都一時預かり事業"
                      value={RESERVE_PLAN_TOKYO_BS_TMP_USE}
                      name="reserve_type"
                      disabled={!canTokyoBsTmpUse}
                      checked={inputTokyoBsTmpUse === 1}
                      onChange={() =>
                        changeReserveTypeHandler(RESERVE_PLAN_TOKYO_BS_TMP_USE)
                      }
                    />
                    {showTokyoBsInformation && (
                      <div className={Style.TimeTmpHoldingInformation}>
                        <div className={Style.TimeTmpHoldingInformationText}>
                          <span>
                            自治体によっては
                            {inputRequestType === REQUEST_TYPE_PICKUP_ONLY &&
                              '送迎サポート'}
                            {inputRequestType === REQUEST_TYPE_SICK &&
                              '病児保育'}
                            {inputRequestType === REQUEST_TYPE_ILLNESS &&
                              '病後児保育'}
                            {inputRequestType === REQUEST_TYPE_HANDICAP &&
                              '障がい児保育'}
                            は助成対象外となります。事前に自治体担当者にお問合せください。
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                オンラインサポートを希望しますか？
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              <div>
                {showSupportStyle && (
                  <label className={Style.TimeTmpHoldingFormCheck}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={Style.TimeTmpHoldingFormCheckIcon}
                    />
                    <input
                      className={Style.TimeTmpHoldingFormCheckCtrl}
                      type="checkbox"
                      value={SUPPORT_STYLE_ONLINE}
                      checked={inputSupportStyle === SUPPORT_STYLE_ONLINE}
                      onChange={() =>
                        changeSupportStyleHandler(
                          inputSupportStyle === SUPPORT_STYLE_ONLINE
                            ? SUPPORT_STYLE_OFFLINE
                            : SUPPORT_STYLE_ONLINE,
                        )
                      }
                    />
                    <div className={Style.TimeTmpHoldingFormCheckText}>
                      希望する
                    </div>
                  </label>
                )}
                {!showSupportStyle && <p className="mb5">オンラインのみ</p>}
              </div>
              <a
                className={Style.TimeTmpHoldingLink}
                href="/help/center_detail/online"
              >
                オンラインサポートとは
              </a>
              {showOnlineSupportInformation && (
                <div
                  className={Style.TimeTmpHoldingInformation}
                  style={{ marginTop: '8px' }}
                >
                  <div className={Style.TimeTmpHoldingInformationTitle}>
                    福利厚生・各種補助について
                  </div>
                  <div className={Style.TimeTmpHoldingInformationText}>
                    オンラインサポートでは、企業型割引券（旧内閣府割引券）、幼保無償化、リロクラブ、すくすくえいど、リソルライフサポート、WELBOX等の福利厚生サポートの申請は「対象外」となります。オンライン家庭教師やオンライン育児相談等は対象外となりますので予めご注意ください。
                  </div>
                </div>
              )}
            </div>
          </div>
          {inputRequestType !== REQUEST_TYPE_HOUSEKEEPING && (
            <div className={Style.TimeTmpHoldingFormRow}>
              <div className={Style.TimeTmpHoldingFormRowName}>
                <div className={Style.TimeTmpHoldingFormItemTitle}>
                  どのお子様を預けますか？
                </div>
              </div>
              <div className={Style.TimeTmpHoldingFormRowBoby}>
                <div>
                  {children.map((child) =>
                    inputRequestType === REQUEST_TYPE_DOULA ||
                    isNurseryAccept(
                      inputStartDateString,
                      isInterview
                        ? child.date_range.interview_start_date
                        : child.date_range.support_start_date,
                      child.date_range.end_date,
                    ) ? (
                      <label
                        className={Style.TimeTmpHoldingFormCheck}
                        key={`child-${child.id}`}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={Style.TimeTmpHoldingFormCheckIcon}
                        />
                        <input
                          className={Style.TimeTmpHoldingFormCheckCtrl}
                          type="checkbox"
                          value={child.id}
                          {...register(`child`)}
                        />
                        <div className={Style.TimeTmpHoldingFormCheckText}>
                          {child.gender === 'male' && '男の子 '}
                          {child.gender === 'female' && '女の子 '}
                          {child.gender !== 'male' &&
                            child.gender !== 'female' &&
                            '性別不明 '}
                          {calcAgeWithDateString(child.birthday)}
                        </div>
                      </label>
                    ) : (
                      <label
                        className={Style.TimeTmpHoldingFormCheck}
                        key={`child-${child.id}`}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className={Style.TimeTmpHoldingFormCheckIcon}
                        />
                        <input
                          className={Style.TimeTmpHoldingFormCheckCtrl}
                          type="checkbox"
                          disabled={true}
                          {...register(`child.${child.id}`)}
                        />
                        <div
                          className={Style.TimeTmpHoldingFormCheckTextDisabled}
                        >
                          {child.gender === 'male' && '男の子 '}
                          {child.gender === 'female' && '女の子 '}
                          {child.gender !== 'male' &&
                            child.gender !== 'female' &&
                            '性別不明 '}
                          {calcAgeWithDateString(child.birthday)}

                          <div>(年齢対象外)</div>
                          <div>
                            {/* 2012年02月01日分より事前面談のみ可能 */}
                            {formatDateString(
                              child.date_range.interview_start_date,
                              'yyyy年MM月dd分',
                            )}
                            より事前面談のみ可能
                            <br />
                            {/* 2012年03月01日分より単発/定期のリクエスト可能 */}
                            {formatDateString(
                              child.date_range.support_start_date,
                              'yyyy年MM月dd分',
                            )}
                            より事前面談のみ可能
                          </div>
                        </div>
                      </label>
                    ),
                  )}
                </div>
                <div className={Style.TimeTmpHoldingChildInformation}>
                  受入可能人数：最大 {sitterProfile.acceptable_number}人
                  <br />
                  {inputRequestType === REQUEST_TYPE_DOULA
                    ? sitterProfile.doula_nursery_from_to
                    : sitterProfile.nursery_from_to}
                  {showInterview && (
                    <>
                      <br />
                      ※事前面談は
                      {calcAgeWithDateString(sitterProfile.interview_from_date)}
                      から予約可能です
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {inputRequestType === REQUEST_TYPE_HOUSEKEEPING && (
            <div className={Style.TimeTmpHoldingFormRow}>
              <div className={Style.TimeTmpHoldingFormRowName}>
                <div className={Style.TimeTmpHoldingFormItemTitleRequired}>
                  サポート時の対応者
                </div>
              </div>
              <div className={Style.TimeTmpHoldingFormRowBoby}>
                <InputText {...register('reserve.person_at_home')} />
                <div className={Style.TimeTmpHoldingExplain}>
                  ※定期予約の場合は初回サポート時の対応者をご入力ください。
                  <br />
                  サポート開始時に依頼内容を直接確認させていただいております。サポート開始時と終了時は、ご在宅での対応をお願いします。
                </div>
              </div>
            </div>
          )}
          {inputRequestType !== REQUEST_TYPE_HOUSEKEEPING && (
            <div className={Style.TimeTmpHoldingFormRow}>
              <div className={Style.TimeTmpHoldingFormRowName}>
                <div className={Style.TimeTmpHoldingFormItemTitleRequired}>
                  具体的な内容
                </div>
              </div>
              <div className={Style.TimeTmpHoldingFormRowBoby}>
                <div className={Style.TimeTmpHoldingFormComment}>
                  <ul className={Style.TimeTmpHoldingFormCommentExamples}>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(lastReserveComment)
                      }
                    >
                      前回の内容
                    </li>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(EXAMPLE_RESERVE_COMMENT1)
                      }
                    >
                      入力例1
                    </li>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(EXAMPLE_RESERVE_COMMENT2)
                      }
                    >
                      入力例2
                    </li>
                  </ul>
                  <textarea
                    className={Style.TimeTmpHoldingFormCommentArea}
                    placeholder={
                      '詳細なサポート内容をご記入ください。\n前回の内容・入力例もご活用いただくとスムーズです。'
                    }
                    rows={6}
                    disabled={false}
                    {...register('reserve.comment')}
                  />
                  <div
                    className={classNames({
                      [Style.TimeTmpHoldingFormCommentRest]: true,
                      [Style.TimeTmpHoldingFormCommentRestAlert]:
                        MAX_LENGTH_COMMENT - inputComment.length < 0,
                    })}
                  >
                    {MAX_LENGTH_COMMENT - inputComment.length}
                  </div>
                </div>
                <div className={Style.TimeTmpHoldingExplain}>
                  サポート内容のご希望、オプションのご希望とメッセージを添えてこちらにご記入ください。
                </div>
              </div>
            </div>
          )}
          {inputRequestType === REQUEST_TYPE_HOUSEKEEPING && (
            <div className={Style.TimeTmpHoldingFormRow}>
              <div className={Style.TimeTmpHoldingFormRowName}>
                <div className={Style.TimeTmpHoldingFormItemTitleRequired}>
                  具体的な内容
                </div>
              </div>
              <div className={Style.TimeTmpHoldingFormRowBoby}>
                <div className={Style.TimeTmpHoldingFormComment}>
                  <ul className={Style.TimeTmpHoldingFormCommentExamples}>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(lastHkReserveComment)
                      }
                    >
                      前回の内容
                    </li>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(EXAMPLE_HK_RESERVE_COMMENT1)
                      }
                    >
                      入力例(掃除)
                    </li>
                    <li
                      className={Style.TimeTmpHoldingFormCommentExample}
                      onClick={() =>
                        clickExampleCommentHandler(EXAMPLE_HK_RESERVE_COMMENT2)
                      }
                    >
                      入力例(料理)
                    </li>
                  </ul>
                  <textarea
                    className={Style.TimeTmpHoldingFormCommentArea}
                    placeholder={
                      '詳細なサポート内容をご記入ください。\n前回の内容・入力例もご活用いただくとスムーズです。'
                    }
                    rows={6}
                    disabled={false}
                    {...register('reserve.comment')}
                  />
                  <div
                    className={classNames({
                      [Style.TimeTmpHoldingFormCommentRest]: true,
                      [Style.TimeTmpHoldingFormCommentRestAlert]:
                        MAX_LENGTH_COMMENT - inputComment.length < 0,
                    })}
                  >
                    {MAX_LENGTH_COMMENT - inputComment.length}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}>
              <div className={Style.TimeTmpHoldingFormItemTitle}>
                カメラ等の設置
              </div>
            </div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              <div className={Style.TimeTmpHoldingDescription}>
                サポート時の様子を撮影・録画する場合には、事前に双方の同意と
                <a
                  href="http://acsa.jp/images/babysitter/2020/webcam_guideline.pdf"
                  target="_blank"
                >
                  ガイドライン
                </a>
                の遵守が必要です。予約前に必ずウェブカメラ等を利用する旨の承諾を得てご利用ください。
              </div>
              <div className="form-radio">
                <InputRadio
                  label="カメラ等を設置する"
                  value={1}
                  {...register(
                    'reserve.security_option.video_recording.requested',
                  )}
                />
                <InputRadio
                  label="カメラ等を設置しない"
                  value={0}
                  {...register(
                    'reserve.security_option.video_recording.requested',
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        {showTokyoBsCaution && (
          <div className={Style.TimeTmpHoldingFormRow}>
            <div className={Style.TimeTmpHoldingFormRowName}></div>
            <div className={Style.TimeTmpHoldingFormRowBoby}>
              <InformationText
                title="東京都一時預かり事業で「複数の未就学児童」の保育=共同保育を依頼する際は、以下をご確認ください。"
                color="warning"
              >
                <div className={Style.TimeTmpHoldingFormTokyoBsCaution}>
                  <p className={Style.TimeTmpHoldingFormTokyoBsCautionTitle}>
                    ①保護者在宅の場合
                  </p>
                  <p>
                    保護者が必ず保育場所に同席し、サポーターと共同で保育を行ってください。
                  </p>
                  <p className={Style.TimeTmpHoldingFormTokyoBsCautionTitle}>
                    ②保護者不在の場合
                  </p>
                  <p>お子様1人につき1名のサポーターをご依頼ください。</p>
                  <p className={Style.TimeTmpHoldingFormTokyoBsCautionTitle}>
                    ③共同保育時の禁止事項
                  </p>
                  <p>
                    保育以外の行為＝家事やテレワーク等を行うことは禁止です。必ず保育に参加してください。
                  </p>
                </div>
              </InformationText>
            </div>
          </div>
        )}
      </section>
      <section className={Style.TimeTmpHoldingSection}>
        <div className={Style.TimeTmpHoldingFormRow}>
          <div className={Style.TimeTmpHoldingFormRowAction}>
            <a
              className={Style.TimeTmpHoldingLinkButton}
              href="#"
              onClick={cancelHandler}
            >
              仮押さえをキャンセルする
            </a>
          </div>
          <div className={Style.TimeTmpHoldingFormRowAction}>
            <InputSubmit value="予約リクエストを送る" disabled={!canSubmit} />
          </div>
        </div>
      </section>
    </form>
  )
}

export default TimeTmpHolding
